import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Divide, Coins, X } from 'lucide-react';
import apiService from '../../services/api';
import { createCaisseIdempotencyKey } from '../../utils/caisseIdempotency';

function lineTotal(line) {
  const v = line.prixFinal != null ? line.prixFinal : line.prixTotal;
  return Number(v) || 0;
}

function toCents(amount) {
  return Math.round(Math.round(Number(amount) * 100) / 1);
}

function fromCents(cents) {
  return Math.round(Number(cents)) / 100;
}

/** Même algorithme que le serveur (plus fortes restes). */
function splitCentsEvenly(totalCents, parts) {
  const base = Math.floor(totalCents / parts);
  let rem = totalCents - base * parts;
  return Array.from({ length: parts }, () => {
    const extra = rem > 0 ? 1 : 0;
    if (rem > 0) rem -= 1;
    return base + extra;
  });
}

export default function RestaurantSplitModal({
  open,
  onClose,
  commercantId,
  serviceId,
  addition,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);
  const [parts, setParts] = useState(2);
  const [assignments, setAssignments] = useState([]);
  /** manual | equal_amount */
  const [splitMode, setSplitMode] = useState('manual');

  const lines = ticket?.produits || ticket?.ticket?.produits || [];

  const loadTicket = useCallback(async () => {
    if (!addition?.id || !commercantId) return;
    setError('');
    setLoading(true);
    try {
      const detail = await apiService.getTicketDetail(addition.id, commercantId);
      const t = detail.ticket || detail;
      setTicket(t);
      const initial = (t.produits || []).map((l) => ({
        ligneId: l.id,
        label: l.produit?.nom || l.designationSnapshot || 'Article',
        qty: l.quantite,
        total: lineTotal(l),
        guestIndex: 0,
      }));
      setAssignments(initial);
      setSplitMode('manual');
    } catch (e) {
      setError(e.message || 'Impossible de charger l’addition');
    } finally {
      setLoading(false);
    }
  }, [addition?.id, commercantId]);

  useEffect(() => {
    if (open) loadTicket();
  }, [open, loadTicket]);

  const guestLabels = useMemo(
    () => Array.from({ length: parts }, (_, i) => `Convive ${i + 1}`),
    [parts]
  );

  const grossLineCents = useMemo(() => {
    if (!ticket) return 0;
    return (lines || []).reduce((s, l) => s + toCents(lineTotal(l)), 0);
  }, [ticket, lines]);

  const reductionCents = useMemo(
    () => toCents(Number(ticket?.reduction) || 0),
    [ticket?.reduction]
  );

  const equalAmountPreview = useMemo(() => {
    const payable = Math.max(0, grossLineCents - reductionCents);
    return splitCentsEvenly(payable, parts).map(fromCents);
  }, [grossLineCents, reductionCents, parts]);

  const previewByGuest = useMemo(() => {
    if (splitMode === 'equal_amount') return equalAmountPreview;
    const sums = Array.from({ length: parts }, () => 0);
    for (const row of assignments) {
      const idx = Math.min(Math.max(0, row.guestIndex), parts - 1);
      sums[idx] += row.total / Math.max(1, row.qty);
    }
    return sums;
  }, [assignments, parts, splitMode, equalAmountPreview]);

  const applyEqualUnitSplit = () => {
    setSplitMode('manual');
    const next = [];
    let unitCursor = 0;
    for (const line of lines) {
      const q = Math.max(0, Math.floor(Number(line.quantite) || 0));
      const unit = lineTotal(line) / Math.max(1, q);
      for (let u = 0; u < q; u += 1) {
        const guestIndex = unitCursor % parts;
        unitCursor += 1;
        next.push({
          ligneId: line.id,
          label: line.produit?.nom || 'Article',
          qty: 1,
          total: unit,
          guestIndex,
        });
      }
    }
    setAssignments(next);
  };

  const selectEqualAmountMode = () => {
    setSplitMode('equal_amount');
    setError('');
  };

  const buildBucketsPayload = () => {
    const buckets = guestLabels.map((label) => ({ label, lines: [] }));
    for (const row of assignments) {
      const bucket = buckets[row.guestIndex];
      const existing = bucket.lines.find((l) => l.ligneId === row.ligneId);
      if (existing) existing.quantite += row.qty;
      else bucket.lines.push({ ligneId: row.ligneId, quantite: row.qty });
    }
    return buckets.filter((b) => b.lines.length > 0);
  };

  const submitSplit = async () => {
    if (!ticket || !serviceId) return;
    setError('');
    setLoading(true);
    try {
      const idempotencyKey = createCaisseIdempotencyKey('restaurant-split');
      let body;
      if (splitMode === 'equal_amount') {
        body = {
          mode: 'EQUAL_AMOUNT',
          parts,
          expectedPosRevision: Number(ticket.posRevision) || 1,
          idempotencyKey,
        };
      } else {
        const buckets = buildBucketsPayload();
        if (buckets.length < 2) {
          setError('Répartissez sur au moins 2 convives.');
          setLoading(false);
          return;
        }
        body = {
          mode: 'BUCKETS',
          expectedPosRevision: Number(ticket.posRevision) || 1,
          idempotencyKey,
          buckets,
        };
      }
      const res = await apiService.splitRestaurantAddition(serviceId, addition.id, body, {
        idempotencyKey,
        commercantId,
      });
      onSuccess?.(res);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Partage refusé');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const requestClose = () => {
    if (loading) return;
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center p-4"
      role="presentation"
      onClick={requestClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-5 space-y-4"
        data-testid="restaurant-split-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" aria-hidden />
            Partager l’addition
          </h2>
          <button type="button" className="min-h-[44px] min-w-[44px] rounded-xl border" onClick={onClose} aria-label="Fermer">
            <X className="w-5 h-5 mx-auto" />
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-3 py-2" role="alert">
            {error}
          </p>
        )}

        {loading && !ticket && <p className="text-sm text-slate-500">Chargement…</p>}

        {ticket && (
          <>
            <p className="text-sm text-slate-600">
              Total {Number(ticket.total).toFixed(2)} € · révision {ticket.posRevision}
            </p>

            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-sm flex items-center gap-2">
                Convives
                <input
                  type="number"
                  min={2}
                  max={12}
                  className="w-16 border rounded-lg px-2 min-h-[44px]"
                  value={parts}
                  onChange={(e) => setParts(Math.min(12, Math.max(2, Number(e.target.value) || 2)))}
                  data-testid="restaurant-split-parts"
                />
              </label>
              <button
                type="button"
                className={`min-h-[44px] px-4 rounded-xl text-sm flex items-center gap-1 ${
                  splitMode === 'equal_amount' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-900'
                }`}
                onClick={selectEqualAmountMode}
                data-testid="restaurant-split-equal-amount"
              >
                <Coins className="w-4 h-4" />
                Partager le montant à parts égales
              </button>
              <button
                type="button"
                className="min-h-[44px] px-4 rounded-xl bg-slate-900 text-white text-sm flex items-center gap-1"
                onClick={applyEqualUnitSplit}
                data-testid="restaurant-split-equal"
              >
                <Divide className="w-4 h-4" />
                Parts égales (articles)
              </button>
            </div>

            {splitMode === 'equal_amount' ? (
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                Chaque convive paiera sa part exacte sur une addition distincte. Les marchandises restent sur
                l’addition d’origine ; les autres parts sont des quote-parts monétaires (sans double stock).
              </p>
            ) : (
              <ul className="space-y-2" data-testid="restaurant-split-lines">
                {assignments.map((row, idx) => (
                  <li key={`${row.ligneId}-${idx}`} className="border rounded-xl p-3 flex flex-wrap gap-2 items-center">
                    <span className="flex-1 text-sm font-medium">{row.label}</span>
                    <span className="text-sm text-slate-600">×{row.qty}</span>
                    <span className="text-sm font-mono">{row.total.toFixed(2)} €</span>
                    <select
                      className="border rounded-lg px-2 min-h-[44px] text-sm"
                      value={row.guestIndex}
                      onChange={(e) => {
                        const guestIndex = Number(e.target.value);
                        setAssignments((prev) =>
                          prev.map((p, i) => (i === idx ? { ...p, guestIndex } : p))
                        );
                      }}
                    >
                      {guestLabels.map((label, gi) => (
                        <option key={label} value={gi}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
            )}

            <div className="grid grid-cols-2 gap-2 text-sm" data-testid="restaurant-split-preview">
              {previewByGuest.map((sum, i) => (
                <div key={guestLabels[i]} className="bg-slate-50 rounded-xl p-2 border">
                  <span className="font-medium">{guestLabels[i]}</span>
                  <span className="block font-mono">
                    {sum.toFixed(2)} €{splitMode === 'manual' ? ' (approx.)' : ''}
                  </span>
                </div>
              ))}
            </div>

            <button
              type="button"
              disabled={loading}
              className="w-full min-h-[48px] rounded-xl bg-emerald-600 text-white font-medium"
              onClick={() => submitSplit()}
              data-testid="restaurant-split-confirm"
            >
              Confirmer le partage
            </button>
          </>
        )}
      </div>
    </div>
  );
}
